import { API_ENDPOINTS, COIN_IDS, ERROR_MESSAGES } from './constants';
import { fallbackData } from '../fallbackData';

// Cache for rate limiting and data
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 5000; // 5 seconds
let cachedPrices = fallbackData;
let lastCacheUpdate = 0;
const CACHE_TTL = 60000; // 1 minute

async function makeRequest(url: string, options: RequestInit = {}) {
  // Check if we need to rate limit
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;
  if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
    await new Promise(resolve => setTimeout(resolve, MIN_REQUEST_INTERVAL - timeSinceLastRequest));
  }
  lastRequestTime = Date.now();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache',
        ...options.headers
      }
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error(ERROR_MESSAGES.RATE_LIMIT);
      }
      throw new Error(`API error: ${response.status}`);
    }

    return response.json();
  } catch (error: any) {
    if (error.name === 'AbortError') {
      throw new Error(ERROR_MESSAGES.TIMEOUT);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function fetchCurrentPrices(forceRefresh = false) {
  try {
    // Check if we can use cached data
    const now = Date.now();
    if (!forceRefresh && now - lastCacheUpdate < CACHE_TTL) {
      return cachedPrices;
    }

    // Check online status
    if (!navigator.onLine) {
      console.debug('Offline, using cached data');
      return cachedPrices;
    }

    const data = await makeRequest(
      `${API_ENDPOINTS.COINGECKO}/simple/price?ids=${Object.values(COIN_IDS).join(',')}&vs_currencies=usd&include_24h_change=true`
    );

    // Format and validate the data
    const formattedData = {
      BTC: {
        price: Number(data.bitcoin?.usd) || fallbackData.BTC.price,
        change24h: Number(data.bitcoin?.usd_24h_change) || 0,
        networkHashrate: fallbackData.BTC.networkHashrate,
        blockReward: fallbackData.BTC.blockReward,
        blockTime: fallbackData.BTC.blockTime,
        algorithm: fallbackData.BTC.algorithm
      },
      LTC: {
        price: Number(data.litecoin?.usd) || fallbackData.LTC.price,
        change24h: Number(data.litecoin?.usd_24h_change) || 0,
        networkHashrate: fallbackData.LTC.networkHashrate,
        blockReward: fallbackData.LTC.blockReward,
        blockTime: fallbackData.LTC.blockTime,
        algorithm: fallbackData.LTC.algorithm
      },
      ETH: {
        price: Number(data.ethereum?.usd) || fallbackData.ETH.price,
        change24h: Number(data.ethereum?.usd_24h_change) || 0,
        networkHashrate: fallbackData.ETH.networkHashrate,
        blockReward: fallbackData.ETH.blockReward,
        blockTime: fallbackData.ETH.blockTime,
        algorithm: fallbackData.ETH.algorithm
      },
      KDA: {
        price: Number(data.kadena?.usd) || fallbackData.KDA.price,
        change24h: Number(data.kadena?.usd_24h_change) || 0,
        networkHashrate: fallbackData.KDA.networkHashrate,
        blockReward: fallbackData.KDA.blockReward,
        blockTime: fallbackData.KDA.blockTime,
        algorithm: fallbackData.KDA.algorithm
      }
    };

    // Validate prices
    const hasValidPrices = Object.values(formattedData).every(coin => 
      typeof coin.price === 'number' && !isNaN(coin.price) && coin.price > 0
    );

    if (!hasValidPrices) {
      console.warn('Invalid price data, using fallback');
      return cachedPrices;
    }

    // Update cache
    cachedPrices = formattedData;
    lastCacheUpdate = now;

    return formattedData;
  } catch (error: any) {
    console.warn('Price fetch error:', error);
    // Return cached data on error
    return cachedPrices;
  }
}

export async function fetchMarketData(forceRefresh = false) {
  try {
    if (!navigator.onLine) {
      throw new Error(ERROR_MESSAGES.OFFLINE);
    }

    const data = await makeRequest(
      `${API_ENDPOINTS.COINGECKO}/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=100&sparkline=false`
    );

    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('Invalid market data received');
    }

    return data;
  } catch (error: any) {
    console.warn('Market data fetch error:', error);
    throw error;
  }
}

// Price history cache
const priceHistoryCache = new Map<string, { data: any[]; timestamp: number }>();
const PRICE_HISTORY_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export async function fetchPriceHistory(coinId: string, days: number = 1) {
  try {
    // Check cache first
    const cacheKey = `${coinId}-${days}`;
    const cached = priceHistoryCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < PRICE_HISTORY_CACHE_TTL) {
      return cached.data;
    }

    // Check online status
    if (!navigator.onLine) {
      throw new Error(ERROR_MESSAGES.OFFLINE);
    }

    const data = await makeRequest(
      `${API_ENDPOINTS.COINGECKO}/coins/${coinId}/market_chart?vs_currency=usd&days=${days}&interval=hourly`
    );

    if (!data?.prices || !Array.isArray(data.prices)) {
      throw new Error('Invalid price history data format');
    }

    const formattedData = data.prices.map(([timestamp, price]: [number, number]) => ({
      timestamp,
      price
    }));

    // Update cache
    priceHistoryCache.set(cacheKey, {
      data: formattedData,
      timestamp: Date.now()
    });

    return formattedData;
  } catch (error: any) {
    console.warn('Price history fetch error:', error);
    // Return empty array instead of throwing
    return [];
  }
}